import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  getCatalogDecorationUrl,
  MAP_NOTE_PIN_BANNERS,
  MAP_NOTE_PIN_COLOR_LABEL,
  MAP_NOTE_PIN_COLORS,
  MAP_NOTE_PIN_OVERLAY_ID,
  type MapNotePinColor,
} from "@west-marches/shared";

import { cn } from "@/lib/classNames";

interface MapNoteMarkerPickerProps {
  /** Currently selected marker. Highlighted in the picker grid. */
  value: MapNotePinColor;
  onChange: (next: MapNotePinColor) => void;
  /** Optional label/title applied to the trigger button. */
  triggerTitle?: string;
  triggerLabel?: string;
  /** Marker shown on the trigger button itself; defaults to the plain banner. */
  triggerMarker?: MapNotePinColor;
  disabled?: boolean;
  className?: string;
}

interface DropdownPosition {
  top: number;
  left: number;
}

const DROPDOWN_GAP_PX = 4;
const DROPDOWN_VIEWPORT_PADDING_PX = 8;
const TRIGGER_SIZE_PX = 24;
const SWATCH_SIZE_PX = 36;

const MARKER_GROUPS: Array<{ label: string; markers: readonly MapNotePinColor[] }> = [
  { label: "Pins", markers: MAP_NOTE_PIN_COLORS },
  { label: "Banners", markers: MAP_NOTE_PIN_BANNERS },
];

export function MapNoteMarkerPicker({
  value,
  onChange,
  triggerTitle = "Pick marker",
  triggerLabel,
  triggerMarker = "banner",
  disabled = false,
  className,
}: MapNoteMarkerPickerProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const triggerImgUrl = getCatalogDecorationUrl(MAP_NOTE_PIN_OVERLAY_ID[triggerMarker]);

  useLayoutEffect(() => {
    if (!open) return;

    function recompute() {
      const triggerEl = triggerRef.current;
      const dropdownEl = dropdownRef.current;
      if (!triggerEl) return;
      const triggerRect = triggerEl.getBoundingClientRect();
      const dropdownWidth = dropdownEl?.offsetWidth ?? 0;
      const dropdownHeight = dropdownEl?.offsetHeight ?? 0;
      // Anchor the dropdown above the trigger so it doesn't get clipped by
      // map chrome that sits below the toolbar/panel.
      const desiredTop = triggerRect.top - dropdownHeight - DROPDOWN_GAP_PX;
      const fallbackTop = triggerRect.bottom + DROPDOWN_GAP_PX;
      const top =
        desiredTop >= DROPDOWN_VIEWPORT_PADDING_PX ? desiredTop : fallbackTop;
      const desiredLeft = triggerRect.right - dropdownWidth;
      const maxLeft = window.innerWidth - dropdownWidth - DROPDOWN_VIEWPORT_PADDING_PX;
      const clampedLeft = Math.max(
        DROPDOWN_VIEWPORT_PADDING_PX,
        Math.min(desiredLeft, maxLeft),
      );
      setPosition({ top, left: clampedLeft });
    }

    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function isInsideMenu(target: EventTarget | null): boolean {
      if (!(target instanceof Node)) return false;
      if (triggerRef.current?.contains(target)) return true;
      if (dropdownRef.current?.contains(target)) return true;
      return false;
    }

    function handleDocPointerDown(event: PointerEvent) {
      if (isInsideMenu(event.target)) return;
      setOpen(false);
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", handleDocPointerDown, true);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("pointerdown", handleDocPointerDown, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const dropdown =
    open && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={dropdownRef}
            role="dialog"
            aria-label="Marker picker"
            style={{
              position: "fixed",
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              visibility: position ? "visible" : "hidden",
            }}
            className={cn(
              "z-200 flex flex-col gap-3 rounded-md border-2 border-neutral-950 bg-[#fffaf1] p-3",
              "shadow-[4px_4px_0_rgba(19,19,19,0.16)]",
            )}
          >
            {MARKER_GROUPS.map((group) => (
              <div key={group.label} className="flex flex-col gap-1.5">
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-600">
                  {group.label}
                </div>
                <div
                  role="listbox"
                  aria-label={group.label}
                  className="flex flex-row flex-wrap gap-1"
                >
                  {group.markers.map((marker) => {
                    const url = getCatalogDecorationUrl(MAP_NOTE_PIN_OVERLAY_ID[marker]);
                    const selected = marker === value;
                    return (
                      <button
                        key={marker}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        title={MAP_NOTE_PIN_COLOR_LABEL[marker]}
                        onClick={(event) => {
                          event.stopPropagation();
                          onChange(marker);
                          setOpen(false);
                        }}
                        className={cn(
                          "inline-flex items-center justify-center rounded border-2 p-1 transition-colors",
                          selected
                            ? "border-neutral-950 bg-[#fff2d9]"
                            : "border-transparent hover:border-neutral-950/30 hover:bg-[#fff2d9]/70",
                        )}
                      >
                        {url ? (
                          <img
                            src={url}
                            alt={MAP_NOTE_PIN_COLOR_LABEL[marker]}
                            width={SWATCH_SIZE_PX}
                            height={SWATCH_SIZE_PX}
                            style={{
                              width: SWATCH_SIZE_PX,
                              height: SWATCH_SIZE_PX,
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <span
                            className="block rounded-full border border-neutral-950/30"
                            style={{
                              width: SWATCH_SIZE_PX,
                              height: SWATCH_SIZE_PX,
                            }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      data-map-note-popover-stop-click="true"
      className={cn("relative inline-flex items-center", className)}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={triggerLabel ?? triggerTitle}
        title={triggerTitle}
        className={cn(
          "inline-flex items-center justify-center rounded-md border-2 border-neutral-950/40 bg-[#fffaf1] p-1 transition-colors",
          "hover:border-neutral-950 disabled:cursor-not-allowed disabled:opacity-60",
        )}
        style={{ width: TRIGGER_SIZE_PX + 8, height: TRIGGER_SIZE_PX + 8 }}
      >
        {triggerImgUrl ? (
          <img
            src={triggerImgUrl}
            alt=""
            width={TRIGGER_SIZE_PX}
            height={TRIGGER_SIZE_PX}
            style={{
              width: TRIGGER_SIZE_PX,
              height: TRIGGER_SIZE_PX,
              objectFit: "contain",
            }}
          />
        ) : (
          <span
            className="block rounded-full border border-neutral-950/30"
            style={{ width: TRIGGER_SIZE_PX, height: TRIGGER_SIZE_PX }}
          />
        )}
      </button>
      {dropdown}
    </div>
  );
}
